import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/TaxPreparation.css';

const TaxPreparation = () => {
  const { openContactModal } = useModal();

  return (
    <div className="tax-preparation">
      {/* Hero Section */}
      <section className="tax-hero">
        <div className="tax-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="tax-hero-content"
          >
            <div className="hero-badge">
              🏛️ Expand Your Tax Practice
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Advanced <span class=\"gradient-text\">Tax Preparation</span><br />Support for CPA Firms" }}
            />
            
            <p className="hero-subtitle">
              Enhance your firm's tax capabilities with our specialized preparation services for complex corporate and multi-state returns. Our certified tax professionals work seamlessly with your team to handle overflow work and complex engagements at 50% lower costs.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'Tax Preparation Services',
                  'Tax Preparation Page - Schedule Tax Consultation'
                )}
              >
                Schedule Tax Consultation
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'Service Portfolio Request',
                  'Tax Preparation Page - View Service Portfolio'
                )}
              >
                View Service Portfolio
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="tax-section">
        <div className="tax-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="tax-section-title gradient-text">Specialized Tax Preparation Services</h2>
            <p className="tax-section-subtitle">
              Expert tax preparation support that seamlessly integrates with your firm's workflow and quality standards
            </p>
            
            <div className="tax-services-grid">
              
              <div className="tax-glass-card">
                <h3>Complex Corporate Returns</h3>
                <p>Specialized preparation of complex corporate returns including C-Corps, S-Corps, partnerships, and LLCs with multi-state complexity.</p>
                <ul>
                  <li>✓ Form 1120/1120S preparation using Drake, Lacerte, ProSeries</li>
                  <li>✓ Multi-state tax allocation and apportionment</li>
                  <li>✓ Complex entity structures and consolidations</li>
                  <li>✓ ASC 740 provision calculations</li>
                </ul>
              </div>

              <div className="tax-glass-card">
                <h3>R&D Credit & Incentives</h3>
                <p>Specialized R&D credit calculations, cost segregation studies, and other tax incentive programs to maximize client benefits.</p>
                <ul>
                  <li>✓ R&D credit studies and calculations</li>
                  <li>✓ Cost segregation analysis</li>
                  <li>✓ Work opportunity tax credits</li>
                  <li>✓ Section 199A deduction optimization</li>
                </ul>
              </div>

              <div className="tax-glass-card">
                <h3>Multi-State Compliance</h3>
                <p>Complete multi-state tax compliance including nexus analysis, state-specific requirements, and complex allocation methods.</p>
                <ul>
                  <li>✓ State income tax returns (all 50 states)</li>
                  <li>✓ Nexus studies and compliance analysis</li>
                  <li>✓ Sales and use tax management</li>
                  <li>✓ State-specific credit and incentive programs</li>
                </ul>
              </div>

              <div className="tax-glass-card">
                <h3>Tax Research & Planning</h3>
                <p>Advanced tax research and strategic planning support to help your firm provide value-added advisory services.</p>
                <ul>
                  <li>✓ Tax research using RIA, BNA, CCH platforms</li>
                  <li>✓ Tax planning memorandums</li>
                  <li>✓ Transaction structuring analysis</li>
                  <li>✓ IRS audit support and documentation</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technology & Platform Expertise */}
      <section className="tax-section">
        <div className="tax-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="tax-section-title gradient-text">
              Platform Expertise & Competitive Advantages
            </h2>
            
            <div className="tax-benefits-grid">
              
              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">💻</div>
                <h4 className="tax-benefit-title">Tax Software Mastery</h4>
                <p className="tax-benefit-description">Certified in all major tax preparation platforms</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Drake • Lacerte • ProSeries • UltraTax • GoSystem • ATX • TaxSlayer Pro
                </div>
              </div>

              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">💰</div>
                <h4 className="tax-benefit-title">50% Cost Reduction</h4>
                <p className="tax-benefit-description">Significant cost savings through offshore delivery model</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Increase margins or offer competitive pricing
                </div>
              </div>

              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">🎓</div>
                <h4 className="tax-benefit-title">Certified Expertise</h4>
                <p className="tax-benefit-description">CPA and Enrolled Agent certified tax professionals</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Continuous education and compliance training
                </div>
              </div>

              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">📅</div>
                <h4 className="tax-benefit-title">Extended Hours Support</h4>
                <p className="tax-benefit-description">Round-the-clock availability during tax seasons</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Meet all deadlines with buffer time
                </div>
              </div>

              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">⚡</div>
                <h4 className="tax-benefit-title">Rapid Turnaround</h4>
                <p className="tax-benefit-description">Handle 5x more returns without additional staff</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Flexible capacity during peak seasons
                </div>
              </div>

              <div className="tax-benefit-card">
                <div className="tax-benefit-icon">🔒</div>
                <h4 className="tax-benefit-title">Confidential Delivery</h4>
                <p className="tax-benefit-description">Seamless white-label service integration</p>
                <div style={{ fontSize: "0.9rem", color: "#888" }}>
                  Bank-level security and data protection
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="tax-section">
        <div className="tax-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="tax-cta-card">
              <h3 className="tax-cta-title">
                Ready to Expand Your Tax Practice?
              </h3>
              <p className="tax-cta-subtitle">
                Join leading CPA firms who have enhanced their tax capabilities with our specialized preparation services. Handle complex returns and busy seasons without hiring additional staff.
              </p>
              <div className="tax-cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'Tax Strategy Call',
                    'Tax Preparation Page - Schedule Tax Strategy Call'
                  )}
                >
                  Schedule Tax Strategy Call
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'Capability Overview Request',
                    'Tax Preparation Page - Request Capability Overview'
                  )}
                >
                  Request Capability Overview
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TaxPreparation;